import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
  InfoCardRow,
} from '@/components/ui/InfoCard.tsx';
import {
  breakdownAmount,
  breakdownToSegments,
  keyedSegmentValue,
} from '@/components/charts/breakdownAdapter.ts';
import { MoneyBreakdownDto } from '@/types/stats/BreakdownDto.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import {
  BALANCE_SEGMENT_REGISTRY,
  BalanceSegmentKey,
} from '@/features/dashboard/tours/types/finance/balanceSegments.utils.ts';
import {
  financeMetricLabel,
  FinanceMetricKey,
} from '@/features/dashboard/tours/types/finance/financeMetrics.utils.ts';
import { findMetric, formatMetricValue } from '@/types/stats/metric.utils.ts';
import BreakdownHeadline from '@/features/dashboard/tours/components/finance/BreakdownHeadline.tsx';
import SegmentBreakdown from '@/features/dashboard/tours/components/finance/SegmentBreakdown.tsx';
import { pluralize } from '@/utils/pluralize.ts';

interface PaymentsBalanceCardProps {
  balance: MoneyBreakdownDto;
}

/**
 * Saldo wpłat w jednej walucie. Karta jest jedna na walutę i nic tu się nie
 * sumuje — euro rozlicza się osobno, bez przeliczania po kursie.
 */
const PaymentsBalanceCard = ({ balance }: PaymentsBalanceCardProps) => {
  const currencyLabel = getCurrencyLabel(balance.currency);
  const paid = keyedSegmentValue(balance, BalanceSegmentKey.PAID);
  const paymentCount = findMetric(
    balance.metrics,
    FinanceMetricKey.PAYMENT_COUNT,
  );
  const overpayment = findMetric(
    balance.metrics,
    FinanceMetricKey.OVERPAYMENT,
    balance.currency,
  );

  // Kontrakt nie niesie wolnego tekstu, więc dopisek „12 wpłat” składa się tu
  // z metryki i siada na odcinku, którego dotyczy.
  const segments = breakdownToSegments(balance, BALANCE_SEGMENT_REGISTRY).map(
    (segment) =>
      segment.id === BalanceSegmentKey.PAID && paymentCount
        ? {
            ...segment,
            note: pluralize(paymentCount.value, 'wpłata', 'wpłaty', 'wpłat'),
          }
        : segment,
  );

  return (
    <InfoCard>
      <InfoCardHeader title={currencyLabel}>
        <span className="mt-1 block text-[13px] text-neutral-400">
          raty w {currencyLabel}
        </span>
      </InfoCardHeader>

      <InfoCardBody className="gap-3.5">
        <BreakdownHeadline
          value={breakdownAmount(balance, paid)}
          total={breakdownAmount(balance, balance.total)}
        />

        <SegmentBreakdown
          total={breakdownAmount(balance, balance.total)}
          segments={segments}
        />

        {overpayment && (
          <InfoCardRow label={financeMetricLabel(FinanceMetricKey.OVERPAYMENT)}>
            <span className="font-semibold tabular-nums text-base-text">
              {formatMetricValue(overpayment)}
            </span>
          </InfoCardRow>
        )}
      </InfoCardBody>
    </InfoCard>
  );
};

export default PaymentsBalanceCard;
